import React, { useEffect, useState } from 'react';
import axios from 'axios';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from './ui/dialog';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Alert, AlertDescription } from './ui/alert';
import {
  Shield, ShieldCheck, AlertTriangle, Loader2, CheckCircle2, XCircle, FileText, MapPin,
} from 'lucide-react';
import { useToast } from '../hooks/use-toast';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Labels FR pour les codes paymentMethod du payload FNE/DGI
const PAYMENT_METHOD_LABEL_FR = {
  cash: 'espèce',
  check: 'chèque',
  transfer: 'virement bancaire',
  'mobile-money': 'mobile money',
  mobile_money: 'mobile money',
  card: 'carte bancaire',
  deferred: 'à terme',
};
const paymentMethodLabelFr = (code) => {
  if (!code) return '—';
  return PAYMENT_METHOD_LABEL_FR[code] || PAYMENT_METHOD_LABEL_FR[String(code).toLowerCase()] || code;
};

/**
 * Modal pré-flight + certification FNE pour une opération spot ou facture contrat.
 *
 * Props :
 *  - open: boolean
 *  - onClose: () => void
 *  - onSuccess: (certification) => void
 *  - targetType: 'spot' | 'contract'
 *  - targetId: op_id ou invoice_id
 *  - targetLabel: string (ex: "BAO-OP-...")
 */
export default function FneCertifyModal({
  open, onClose, onSuccess, targetType, targetId, targetLabel = '',
}) {
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [preflight, setPreflight] = useState(null);
  const [error, setError] = useState(null);
  const [sellerName, setSellerName] = useState('');
  // Décision TVA explicite en cas de mismatch : 'exempt' (sans TVA) | 'force_tva'
  const [tvaDecision, setTvaDecision] = useState('exempt');

  useEffect(() => {
    // Récupère le profil pour afficher le sellerName (vendeur qui signe)
    if (!open) return;
    axios.get(`${API}/auth/me`).then(r => {
      const u = r.data || {};
      const name = `${u.firstName || ''} ${u.lastName || ''}`.trim() || u.email || '';
      setSellerName(name);
    }).catch(() => {});
  }, [open]);

  // Reset force TVA toggle when modal closes (avoid useEffect set-state-in-effect)
  const handleClose = () => {
    setTvaDecision('exempt');
    onClose?.();
  };

  useEffect(() => {
    if (!open || !targetId) return;
    setLoading(true);
    setError(null);
    setPreflight(null);
    // Preflight endpoint adapté au type (spot ou contract)
    const url = targetType === 'contract'
      ? `${API}/fne/preflight/contract/${targetId}`
      : `${API}/fne/preflight/spot/${targetId}`;
    axios.get(url)
      .then(r => setPreflight(r.data))
      .catch(err => {
        const msg = err?.response?.data?.detail || err.message;
        setError(typeof msg === 'string' ? msg : 'Erreur de pré-vérification');
      })
      .finally(() => setLoading(false));
  }, [open, targetId, targetType]);

  const handleCertify = async () => {
    if (!preflight?.ok) return;
    const mismatch = !!preflight?.tvaCheck?.mismatch;
    setSubmitting(true);
    try {
      const endpoint = targetType === 'spot'
        ? `${API}/fne/sign-spot/${targetId}`
        : `${API}/fne/sign-contract/${targetId}`;
      let body = {};
      if (mismatch) {
        body = tvaDecision === 'force_tva'
          ? { forceTaxCode: 'TVA', forceReason: 'Override admin: TVA 18% appliquée malgré tvaStatus client' }
          : { forceTaxCode: 'TVAD', forceReason: 'Client exonéré: certification sans TVA, montants alignés sur le HT' };
      }
      const { data } = await axios.post(endpoint, body);
      if (data.success) {
        toast({
          title: 'Facture certifiée FNE',
          description: `Référence : ${data.certification?.reference}`,
        });
        onSuccess?.(data.certification);
        handleClose();
      } else {
        toast({
          title: 'Échec de la certification',
          description: data.errorMessage || 'Erreur inconnue',
          variant: 'destructive',
        });
      }
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const msg = typeof detail === 'object' ? (detail.reason || detail.message) : (detail || err.message);
      toast({
        title: 'Erreur FNE',
        description: msg,
        variant: 'destructive',
      });
    } finally {
      setSubmitting(false);
    }
  };

  const totalLines = preflight?.payloadPreview?.items?.length || 0;
  const totalAmount = (preflight?.payloadPreview?.items || []).reduce(
    (acc, it) => acc + (it.quantity * it.amount), 0
  );
  const env = preflight?.environment || 'dev';
  const isProd = env === 'prod';

  return (
    <Dialog open={open} onOpenChange={(v) => !v && handleClose()}>
      <DialogContent className="max-w-2xl" data-testid="fne-certify-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Shield className="h-5 w-5 text-emerald-600" />
            Certification FNE — Pré-vérification
          </DialogTitle>
          <DialogDescription>
            {targetLabel ? <span className="font-mono">{targetLabel}</span> : null}
          </DialogDescription>
        </DialogHeader>

        {loading && (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-6 w-6 animate-spin text-emerald-600" />
          </div>
        )}

        {error && !loading && (
          <Alert variant="destructive" data-testid="fne-preflight-error">
            <AlertTriangle className="h-4 w-4" />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {preflight && !loading && (
          <div className="space-y-4">
            {/* Bandeau environnement */}
            <div
              className={`flex items-center justify-between rounded-lg border px-4 py-2 text-sm font-medium ${
                isProd
                  ? 'border-rose-300 bg-rose-50 text-rose-800'
                  : 'border-amber-300 bg-amber-50 text-amber-800'
              }`}
              data-testid="fne-env-banner"
            >
              <span>Environnement : <strong>{env.toUpperCase()}</strong></span>
              <Badge variant={isProd ? 'destructive' : 'secondary'}>
                {isProd ? 'PRODUCTION' : 'TEST'}
              </Badge>
            </div>

            {/* Vendeur signataire (auto) */}
            {sellerName && (
              <div className="flex items-center justify-between px-4 py-2 bg-emerald-50 border border-emerald-200 rounded-lg text-sm" data-testid="fne-seller-banner">
                <span className="text-emerald-800">
                  Signé par&nbsp;: <strong>{sellerName}</strong>
                </span>
                <span className="text-[10px] text-emerald-700 italic">clientSellerName — auto</span>
              </div>
            )}

            {/* TVA Check enrichie (2026-02-08) — montants HT/TVA/TTC + mismatch */}
            {preflight?.tvaCheck && (() => {
              const tva = preflight.tvaCheck;
              const isMismatch = !!tva.mismatch;
              const forcingTva = isMismatch && tvaDecision === 'force_tva';
              const effectiveCode = forcingTva ? 'TVA' : tva.effectiveTaxCode;
              const effectiveRate = forcingTva ? 18 : tva.appliedTaxRate;
              const ht = tva.htAmount || 0;
              const computedTva = forcingTva ? Math.round(ht * 0.18) : tva.tvaAmount;
              const computedTtc = ht + computedTva;
              const tvaLabel = {
                TVA: 'TVA 18% (Standard)',
                TVAB: 'TVA 9% (Réduite)',
                TVAC: 'TVAC (Exonération conventionnelle)',
                TVAD: 'TVAD (Exonéré / Sans TVA)',
              }[effectiveCode] || effectiveCode;

              return (
                <div
                  className={`rounded-lg border ${forcingTva ? 'bg-rose-50 border-rose-300' : isMismatch ? 'bg-amber-50 border-amber-300' : 'bg-blue-50 border-blue-200'}`}
                  data-testid="fne-tva-banner"
                >
                  <div className="px-4 py-3 space-y-2">
                    <div className="flex items-start gap-2">
                      <span className="text-lg leading-none">
                        {forcingTva ? '💰' : (isMismatch ? '⚠️' : (effectiveRate > 0 ? '💰' : 'ℹ️'))}
                      </span>
                      <div className="flex-1">
                        <div className="font-semibold text-sm">
                          Régime fiscal : <span className="font-mono">{tvaLabel}</span>
                        </div>
                        {isMismatch && !forcingTva && (
                          <div className="text-xs mt-1 text-amber-800">
                            <strong>Écart détecté.</strong> L&apos;opération porte une TVA {tva.opTauxTva}% alors que le
                            client est <code>{tva.clientTvaStatus || 'non défini'}</code> : la facture sera certifiée
                            sans TVA. Choisissez le régime ci-dessous.
                          </div>
                        )}
                        {forcingTva && (
                          <div className="text-xs mt-1 text-rose-800">
                            <strong>Override actif :</strong> TVA 18 % sera appliquée par la DGI sur le montant HT.
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Tableau HT / TVA / TTC */}
                    <div className="grid grid-cols-3 gap-2 mt-2 text-xs">
                      <div className="bg-white rounded p-2 border" data-testid="fne-tva-ht">
                        <div className="text-slate-500">Total HT</div>
                        <div className="font-semibold text-slate-900">{ht.toLocaleString('fr-FR')} XOF</div>
                      </div>
                      <div className="bg-white rounded p-2 border" data-testid="fne-tva-amount">
                        <div className="text-slate-500">TVA ({effectiveRate}%)</div>
                        <div className={`font-semibold ${computedTva > 0 ? 'text-blue-700' : 'text-slate-400'}`}>
                          {computedTva.toLocaleString('fr-FR')} XOF
                        </div>
                      </div>
                      <div className="bg-white rounded p-2 border" data-testid="fne-tva-ttc">
                        <div className="text-slate-500">Total TTC</div>
                        <div className="font-bold text-emerald-700">{computedTtc.toLocaleString('fr-FR')} XOF</div>
                      </div>
                    </div>

                    {/* Décision TVA explicite (mismatch) */}
                    {isMismatch && (
                      <div className="mt-2 space-y-2" data-testid="fne-tva-decision">
                        <p className="text-xs font-semibold text-slate-700">
                          Choisissez le régime à certifier :
                        </p>
                        <label
                          className={`flex items-start gap-2 p-2 bg-white border-2 rounded cursor-pointer transition hover:bg-emerald-50 ${tvaDecision === 'exempt' ? 'border-emerald-400' : 'border-slate-200'}`}
                          data-testid="fne-exempt-option"
                        >
                          <input
                            type="radio"
                            name="fne-tva-decision"
                            checked={tvaDecision === 'exempt'}
                            onChange={() => setTvaDecision('exempt')}
                            className="mt-0.5 h-4 w-4 accent-emerald-600"
                            data-testid="fne-exempt-radio"
                          />
                          <span className="text-xs text-slate-800">
                            <strong>Certifier sans TVA</strong> — client exonéré ({tva.clientTvaStatus || 'non défini'})<br />
                            <span className="text-slate-600">
                              La facture est émise en {tvaLabel} pour {ht.toLocaleString('fr-FR')} XOF.
                              Les montants de l&apos;opération seront alignés sur ce HT (TVA remise à 0)
                              pour rester cohérents avec la DGI.
                            </span>
                          </span>
                        </label>
                        <label
                          className={`flex items-start gap-2 p-2 bg-white border-2 rounded cursor-pointer transition hover:bg-rose-50 ${tvaDecision === 'force_tva' ? 'border-rose-400' : 'border-slate-200'}`}
                          data-testid="fne-force-tva-toggle"
                        >
                          <input
                            type="radio"
                            name="fne-tva-decision"
                            checked={tvaDecision === 'force_tva'}
                            onChange={() => setTvaDecision('force_tva')}
                            className="mt-0.5 h-4 w-4 accent-rose-600"
                            data-testid="fne-force-tva-checkbox"
                          />
                          <span className="text-xs text-rose-900">
                            <strong>Forcer TVA 18 % normale</strong> (override du statut TVA client)<br/>
                            <span className="text-rose-700">
                              Vous certifiez explicitement que l&apos;opération doit être facturée
                              avec la TVA 18 % malgré le statut <code>{tva.clientTvaStatus || 'non défini'}</code> du client.
                            </span>
                          </span>
                        </label>
                      </div>
                    )}
                  </div>
                </div>
              );
            })()}

            {/* Checklist */}
            <div className="space-y-2">
              <h4 className="text-sm font-semibold text-slate-700">Pré-vérification</h4>
              {preflight.issues.length === 0 ? (
                <div className="flex items-center gap-2 text-emerald-700" data-testid="fne-preflight-ok">
                  <CheckCircle2 className="h-4 w-4" />
                  <span className="text-sm">Toutes les données sont valides</span>
                </div>
              ) : (
                <ul className="space-y-1.5" data-testid="fne-preflight-issues">
                  {preflight.issues.map((iss, idx) => (
                    <li key={idx} className="flex items-start gap-2 text-sm text-rose-700">
                      <XCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
                      <span>
                        <strong>{iss.field}</strong> — {iss.message}
                      </span>
                    </li>
                  ))}
                </ul>
              )}
              {preflight.warnings.length > 0 && (
                <ul className="space-y-1.5 pt-1" data-testid="fne-preflight-warnings">
                  {preflight.warnings.map((w, idx) => (
                    <li key={idx} className="flex items-start gap-2 text-sm text-amber-700">
                      <AlertTriangle className="h-4 w-4 mt-0.5 flex-shrink-0" />
                      <span>{w.message}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            {/* Récap payload */}
            {preflight.payloadPreview && (
              <div className="rounded-lg border bg-slate-50 p-3 space-y-2 text-sm">
                <div className="flex items-center gap-2 font-semibold text-slate-700">
                  <FileText className="h-4 w-4" /> Récapitulatif
                </div>
                <div className="grid grid-cols-2 gap-x-4 gap-y-1.5">
                  <span className="text-slate-500">Template</span>
                  <span className="font-medium">
                    <Badge variant="outline">{preflight.payloadPreview.template}</Badge>
                  </span>
                  <span className="text-slate-500">Client</span>
                  <span className="font-medium">{preflight.payloadPreview.clientCompanyName}</span>
                  {preflight.payloadPreview.clientNcc && (
                    <>
                      <span className="text-slate-500">NCC</span>
                      <span className="font-mono">{preflight.payloadPreview.clientNcc}</span>
                    </>
                  )}
                  <span className="text-slate-500">Mode paiement</span>
                  <span className="font-medium">{paymentMethodLabelFr(preflight.payloadPreview.paymentMethod)}</span>
                  <span className="text-slate-500">TVA</span>
                  <span className="font-medium">{preflight.payloadPreview.taxes}</span>
                  <span className="text-slate-500">Lignes</span>
                  <span className="font-medium">{totalLines}</span>
                  <span className="text-slate-500">Total HT</span>
                  <span className="font-semibold text-emerald-700">
                    {totalAmount.toLocaleString('fr-FR')} XOF
                  </span>
                </div>

                {/* Détail des lignes envoyées à la DGI */}
                {(preflight.payloadPreview.items || []).length > 0 && (
                  <div className="pt-2 border-t border-slate-200">
                    <div className="flex items-center gap-2 font-semibold text-slate-700 mb-1.5">
                      <FileText className="h-4 w-4" /> Lignes facturées ({totalLines})
                    </div>
                    <div className="overflow-x-auto rounded border border-slate-200 bg-white">
                      <table className="w-full text-xs" data-testid="fne-lines-table">
                        <thead>
                          <tr className="bg-slate-100/70 text-slate-500 text-left">
                            <th className="py-1.5 px-2 font-medium">Désignation</th>
                            <th className="py-1.5 px-2 font-medium text-right">Qté</th>
                            <th className="py-1.5 px-2 font-medium text-right">PU HT</th>
                            <th className="py-1.5 px-2 font-medium text-center">Taxe</th>
                            <th className="py-1.5 px-2 font-medium text-right">Total HT</th>
                          </tr>
                        </thead>
                        <tbody>
                          {(preflight.payloadPreview.items || []).map((it, idx) => {
                            const lineTotal = (it.quantity || 0) * (it.amount || 0);
                            return (
                              <tr key={idx} className="border-t border-slate-100" data-testid={`fne-line-${idx}`}>
                                <td className="py-1.5 px-2 text-slate-700">
                                  <div className="font-medium">{it.description || '—'}</div>
                                  {(it.reference || it.measurementUnit) && (
                                    <div className="text-[10px] text-slate-400">
                                      {it.reference ? `Réf ${it.reference}` : ''}
                                      {it.reference && it.measurementUnit ? ' · ' : ''}
                                      {it.measurementUnit || ''}
                                    </div>
                                  )}
                                </td>
                                <td className="py-1.5 px-2 text-right text-slate-600 whitespace-nowrap">{(it.quantity ?? 0).toLocaleString('fr-FR')}</td>
                                <td className="py-1.5 px-2 text-right text-slate-600 whitespace-nowrap">{(it.amount ?? 0).toLocaleString('fr-FR')}</td>
                                <td className="py-1.5 px-2 text-center">
                                  {(it.taxes && it.taxes.length) ? (
                                    <Badge variant="outline" className="text-[10px]">{it.taxes.join(', ')}</Badge>
                                  ) : <span className="text-slate-400">—</span>}
                                </td>
                                <td className="py-1.5 px-2 text-right font-medium text-slate-800 whitespace-nowrap">{lineTotal.toLocaleString('fr-FR')}</td>
                              </tr>
                            );
                          })}
                        </tbody>
                        <tfoot>
                          <tr className="border-t border-slate-200 bg-slate-50">
                            <td className="py-1.5 px-2 font-semibold text-slate-700" colSpan={4}>Total HT</td>
                            <td className="py-1.5 px-2 text-right font-bold text-emerald-700 whitespace-nowrap">{totalAmount.toLocaleString('fr-FR')} XOF</td>
                          </tr>
                        </tfoot>
                      </table>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Stickers */}
            {preflight.stickerBalance !== null && preflight.stickerBalance !== undefined && (
              <div className="text-xs text-slate-500" data-testid="fne-sticker-info">
                Solde stickers : <strong>{preflight.stickerBalance}</strong>
                {preflight.stickerBalance < 100 && (
                  <span className="ml-2 text-amber-700">⚠ Réapprovisionner via fne.dgi.gouv.ci</span>
                )}
              </div>
            )}

            <Alert>
              <AlertTriangle className="h-4 w-4" />
              <AlertDescription className="text-xs">
                Cette action consomme <strong>1 sticker</strong> et est <strong>irréversible</strong> en PROD
                (seul un avoir FNE peut annuler la facture).
              </AlertDescription>
            </Alert>
          </div>
        )}

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={handleClose} disabled={submitting} data-testid="fne-cancel-btn">
            Annuler
          </Button>
          <Button
            onClick={handleCertify}
            disabled={submitting || !preflight?.ok}
            className="bg-emerald-600 hover:bg-emerald-700"
            data-testid="fne-confirm-btn"
          >
            {submitting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            <ShieldCheck className="h-4 w-4 mr-2" />
            {preflight?.tvaCheck?.mismatch && tvaDecision === 'force_tva' ? 'Certifier avec TVA 18%' : (preflight?.tvaCheck?.mismatch ? 'Certifier sans TVA' : 'Confirmer la certification')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
